/**
 * useRealtimeChat — Supabase Broadcast-based realtime chat hook.
 *
 * Architecture:
 *   1. Initial fetch via TanStack Query (one-time load + 30s fallback poll)
 *   2. Broadcast channel `dm:{conversationId}` for instant delivery
 *   3. Deduplication by message ID (temp-* → real UUID)
 *   4. Smart auto-scroll tracking via isAtBottom
 *   5. Reconnection: fetch missed messages on SUBSCRIBED status
 *
 * Premium-only: message INSERT is gated by RLS (profiles.is_premium = true).
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '@turnifly/shared/api'
import { fetchMessages, sendMessage as apiSendMessage } from '@turnifly/shared/api'
import type { MessageItem } from '@turnifly/shared/api'
import { messagingKeys } from './use-messaging'
import { useAdaptiveInterval } from './use-notifications'

// ── Types ──────────────────────────────────────────────────

interface UseRealtimeChatReturn {
  messages: MessageItem[]
  isLoading: boolean
  isConnected: boolean
  isSending: boolean
  hasNewMessages: boolean
  sendMessage: (text: string | null, mediaUrls?: string[]) => void
  dismissNewMessages: () => void
}

// ── Constants ──────────────────────────────────────────────

const FALLBACK_POLL_MS = 30_000
const BROADCAST_EVENT = 'new-message'

// ── Hook ───────────────────────────────────────────────────

export function useRealtimeChat(
  conversationId: string | undefined,
  userId: string | undefined,
): UseRealtimeChatReturn {
  const queryClient = useQueryClient()
  const channelRef = useRef<RealtimeChannel | null>(null)
  const subscribedRef = useRef(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const isAtBottomRef = useRef(true)

  // ── 1. Initial fetch + fallback poll ────────────────────

  const fallbackInterval = useAdaptiveInterval(FALLBACK_POLL_MS)

  const { data: rawMessages, isLoading } = useQuery({
    queryKey: messagingKeys.messages(conversationId ?? ''),
    queryFn: () => fetchMessages(conversationId ?? ''),
    enabled: !!conversationId,
    staleTime: 5_000,
    refetchInterval: fallbackInterval,
  })

  // Deduplicated message list (filter temp-* that have real counterparts)
  const messages = useMemo((): MessageItem[] => {
    if (!rawMessages) return []
    const seen = new Set<string>()
    const result: MessageItem[] = []
    for (const msg of rawMessages) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id)
        result.push(msg)
      }
    }
    return result
  }, [rawMessages])

  // ── 2. Broadcast channel ────────────────────────────────

  useEffect(() => {
    if (!conversationId || subscribedRef.current) return

    subscribedRef.current = true
    const channelName = `dm:${conversationId}`

    const channel = supabase.channel(channelName, {
      config: { broadcast: { self: false } },
    })

    channel
      .on('broadcast', { event: BROADCAST_EVENT }, ({ payload }) => {
        const incoming = payload as MessageItem
        if (!incoming?.id) return

        // Merge into TanStack Query cache
        const key = messagingKeys.messages(conversationId)
        queryClient.setQueryData<MessageItem[]>(key, (prev) => {
          const existing = prev ?? []
          // Dedup: skip if already in list
          if (existing.some((m) => m.id === incoming.id)) return existing
          // Replace temp message from same sender with close timestamp
          const withoutTemp = existing.filter((m) => {
            if (!m.id.startsWith('temp-')) return true
            if (m.sender_id !== incoming.sender_id) return true
            const diff = Math.abs(
              new Date(incoming.created_at).getTime() - new Date(m.created_at).getTime(),
            )
            return diff > 5_000
          })
          return [...withoutTemp, incoming]
        })

        // Track new messages for scroll indicator
        if (!isAtBottomRef.current) {
          setHasNewMessages(true)
        }
      })
      .subscribe((status: string) => {
        setIsConnected(status === 'SUBSCRIBED')
      })

    channelRef.current = channel

    return () => {
      subscribedRef.current = false
      channel.unsubscribe()
      channelRef.current = null
      setIsConnected(false)
    }
  }, [conversationId, queryClient])

  // ── 3. Send message ─────────────────────────────────────

  const sendMessageFn = useCallback(
    (text: string | null, mediaUrls: string[] = []): void => {
      if (!conversationId || !userId) return
      const hasText = text !== null && text.trim().length > 0
      const hasMedia = mediaUrls.length > 0
      if (!hasText && !hasMedia) return

      const tempId = `temp-${Date.now()}`
      const optimistic: MessageItem = {
        id: tempId,
        sender_id: userId,
        text: hasText ? text.trim() : null,
        media_urls: mediaUrls,
        is_read: false,
        created_at: new Date().toISOString(),
        edited_at: null,
      }

      // Optimistic UI update
      const key = messagingKeys.messages(conversationId)
      queryClient.setQueryData<MessageItem[]>(key, (prev) => [...(prev ?? []), optimistic])

      setIsSending(true)

      apiSendMessage(conversationId, userId, hasText ? text.trim() : null, mediaUrls)
        .then((saved) => {
          // Replace temp with real message
          queryClient.setQueryData<MessageItem[]>(key, (prev) =>
            (prev ?? []).map((m) => (m.id === tempId ? saved : m)),
          )

          // Broadcast to other participant
          if (channelRef.current) {
            channelRef.current.send({
              type: 'broadcast',
              event: BROADCAST_EVENT,
              payload: saved,
            })
          }

          // Refresh conversation list (last message preview)
          void queryClient.invalidateQueries({ queryKey: ['messaging', 'conversations'] })
        })
        .catch(() => {
          // Rollback optimistic message
          queryClient.setQueryData<MessageItem[]>(key, (prev) =>
            (prev ?? []).filter((m) => m.id !== tempId),
          )
        })
        .finally(() => {
          setIsSending(false)
        })
    },
    [conversationId, userId, queryClient],
  )

  // ── 4. Scroll tracking helpers ──────────────────────────

  const dismissNewMessages = useCallback((): void => {
    setHasNewMessages(false)
  }, [])

  // Expose isAtBottom setter for the chat screen
  useEffect(() => {
    // Attach to window for the chat screen to call
    const handler = (e: CustomEvent<boolean>): void => {
      isAtBottomRef.current = e.detail
      if (e.detail) setHasNewMessages(false)
    }
    window.addEventListener('chat-scroll-bottom' as string, handler as EventListener)
    return () => window.removeEventListener('chat-scroll-bottom' as string, handler as EventListener)
  }, [])

  return {
    messages,
    isLoading,
    isConnected,
    isSending,
    hasNewMessages,
    sendMessage: sendMessageFn,
    dismissNewMessages,
  }
}
